import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScanLine, FolderOpen, FileImage, Loader2, Trash2, X, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API, withToken } from '@/utils/api';
import axios from 'axios';
import { PhotoViewer } from './PhotoViewer';

const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

export const SLIP_SUPPLIERS = ['ESA', 'Jacquet Automobile', 'TecAuto'];
const SUP_CLS = { ESA: 'bg-blue-100 text-blue-700', 'Jacquet Automobile': 'bg-amber-100 text-amber-700', TecAuto: 'bg-emerald-100 text-emerald-700' };

export const OrderSlipsSection = ({ woId, workOrder, uploadedBy = 'Atelier', onImported }) => {
  const [slips, setSlips] = useState([]);
  const [supplier, setSupplier] = useState('ESA');
  const [uploading, setUploading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const fileRef = useRef(null);

  const loadSlips = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/order-slips/by-work-order/${woId}`);
      setSlips(res.data);
    } catch {}
  }, [woId]);

  useEffect(() => { loadSlips(); }, [loadSlips]);

  // Poll for new slips while QR is shown
  useEffect(() => {
    if (!qrData) return;
    const interval = setInterval(() => {
      loadSlips().then(() => {});
    }, 3000);
    setPollInterval(interval);
    return () => clearInterval(interval);
  }, [qrData, loadSlips]);

  // Detect new slip uploaded via QR
  const prevSlipCount = useRef(slips.length);
  useEffect(() => {
    if (slips.length > prevSlipCount.current && qrData) {
      const n = slips[0]?.imported_count || 0;
      toast.success(n ? `Bon reçu du téléphone : ${n} pièce(s) ajoutée(s)` : 'Bon reçu du téléphone');
      onImported?.();
      setQrData(null);
    }
    prevSlipCount.current = slips.length;
  }, [slips.length, qrData]);

  const handleScanClick = async () => {
    try {
      const res = await axios.post(`${API}/order-slips/create-scan-session`, {
        work_order_id: woId,
        supplier: supplier,
        uploaded_by: uploadedBy,
      });
      const scanUrl = `${FRONTEND_URL}/scan-upload/${res.data.token}`;
      setQrData({ url: scanUrl, session: res.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('work_order_id', woId);
      formData.append('supplier', supplier);
      formData.append('uploaded_by', uploadedBy);
      const r = await axios.post(`${API}/order-slips/upload`, formData, { timeout: 120000 });
      if (r.data.ocr_error) toast.warning(r.data.ocr_error);
      else toast.success(`Bon ${supplier} lu : ${r.data.added} pièce(s) ajoutée(s) à la liste`);
      loadSlips(); onImported?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de telechargement'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteSlip = async (slipId) => {
    if (!window.confirm('Supprimer ce bon de livraison et les pièces qu\'il a ajoutées ?')) return;
    try {
      await axios.delete(`${API}/order-slips/${slipId}`);
      toast.success('Bon supprime');
      loadSlips(); onImported?.();
    } catch { toast.error('Erreur'); }
  };

  const isCompleted = workOrder?.status === 'completed';

  return (
    <Card className="p-4 rounded-xl border-l-4 border-l-indigo-500" data-testid="order-slips-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileImage className="w-4 h-4 text-indigo-500" />
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Bons de livraison ({slips.length})
          </h3>
        </div>
      </div>

      {!isCompleted && (
        <div className="space-y-3 mb-3" data-testid="slip-capture-controls">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium shrink-0">Fournisseur :</span>
            <Select value={supplier} onValueChange={setSupplier}>
              <SelectTrigger className="w-48 h-8 text-sm bg-white" data-testid="slip-supplier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLIP_SUPPLIERS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            data-testid="slip-file-input"
          />

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleScanClick}
              disabled={uploading}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-sm transition-colors disabled:opacity-50 touch-manipulation"
              data-testid="slip-scan-btn"
            >
              <QrCode className="w-6 h-6" />
              Photo avec le téléphone
            </button>

            <button
              type="button"
              onClick={() => !uploading && fileRef.current?.click()}
              disabled={uploading}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-600 hover:bg-slate-700 active:bg-slate-800 text-white font-semibold text-sm transition-colors disabled:opacity-50 touch-manipulation"
              data-testid="slip-file-btn"
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <FolderOpen className="w-6 h-6" />}
              {uploading ? 'Lecture du bon…' : 'Photo / fichier'}
            </button>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {qrData && (
        <div className="mb-4 p-4 bg-white border-2 border-indigo-200 rounded-xl text-center space-y-3" data-testid="qr-code-display">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-indigo-700">Scannez avec votre telephone</p>
            <button onClick={() => setQrData(null)} className="text-slate-400 hover:text-slate-600" data-testid="qr-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex justify-center">
            <div className="p-3 bg-white rounded-xl border border-slate-100">
              <QRCodeSVG value={qrData.url} size={200} />
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500">
              <span className="font-bold">{supplier}</span>
              {' '} — {qrData.session?.vehicle_info}
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-indigo-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              En attente de la photo… les pièces seront ajoutées automatiquement
            </div>
          </div>
        </div>
      )}

      {/* Uploaded slips */}
      {slips.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-3">Aucun bon de livraison — photographiez-le : toutes les pièces sont reprises dans la liste</p>
      ) : (
        <div className="space-y-2">
          {slips.map(slip => (
            <div key={slip.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg" data-testid={`slip-${slip.id}`}>
              <button onClick={() => setViewerPhoto(`${API}/order-slips/file/${slip.filename}`)}
                className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all">
                <img src={withToken(`${API}/order-slips/file/${slip.filename}`)} alt="Bon" className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; const fallback = document.createElement('div'); fallback.className = 'w-full h-full flex items-center justify-center text-red-300 text-xs'; fallback.textContent = 'Image indisponible'; e.target.parentElement.appendChild(fallback); }} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${SUP_CLS[slip.supplier] || 'bg-slate-100 text-slate-700'}`}>{slip.supplier}</span>
                  {slip.number && <span className="text-xs font-mono">N° {slip.number}</span>}
                  <span className="text-xs text-slate-400">{new Date(slip.created_at).toLocaleString('fr-CH')}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Par {slip.uploaded_by}{slip.imported_count != null && <span className="text-emerald-700 font-semibold" data-testid={`slip-count-${slip.id}`}> · {slip.imported_count} pièce(s) ajoutée(s)</span>}{slip.ocr_error && <span className="text-amber-700"> · lecture impossible</span>}</p>
              </div>
              {!isCompleted && (
                <Button variant="ghost" size="sm" onClick={() => deleteSlip(slip.id)}
                  className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-slip-${slip.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Photo Viewer */}
      {viewerPhoto && (
        <PhotoViewer photos={[viewerPhoto]} onClose={() => setViewerPhoto(null)} />
      )}
    </Card>
  );
};
